import { useEffect, useState } from 'preact/hooks';

export type RouteName = 'login' | 'vocab' | 'settings' | 'anki' | 'notFound';

export interface Route {
  name: RouteName;
  /** Original path for the not-found screen. */
  path: string;
}

/** Landing route for an authenticated user (and for an empty hash). */
export const DEFAULT_ROUTE: RouteName = 'vocab';

/** Hash router: `#/vocab`, `#/settings`, `#/anki`, `#/login`.
 *
 * Hash routing (not history) is deliberate: Caddy serves static files, and a
 * hash never reaches the server, so no rewrite rules or 404 fallbacks are needed.
 */
export function parseHash(hash: string): Route {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const path = raw === '' ? '/' : raw;
  const normalised = path.length > 1 ? path.replace(/\/+$/, '') : path;

  switch (normalised) {
    case '/':
    case '':
      return { name: DEFAULT_ROUTE, path: normalised };
    case '/login':
      return { name: 'login', path: normalised };
    case '/vocab':
      return { name: 'vocab', path: normalised };
    case '/settings':
      return { name: 'settings', path: normalised };
    case '/anki':
      return { name: 'anki', path: normalised };
    default:
      return { name: 'notFound', path: normalised };
  }
}

export function routeHref(name: Exclude<RouteName, 'notFound'>): string {
  return `#/${name}`;
}

export function navigate(path: string, options: { replace?: boolean } = {}): void {
  const target = path.startsWith('#') ? path : `#${path.startsWith('/') ? '' : '/'}${path}`;
  if (options.replace === true) {
    const url = `${window.location.pathname}${window.location.search}${target}`;
    window.history.replaceState(null, '', url);
    window.dispatchEvent(new HashChangeEvent('hashchange'));
    return;
  }
  window.location.hash = target;
}

/** Current route, re-rendering on hashchange. */
export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash));

  useEffect(() => {
    const onChange = () => setRoute(parseHash(window.location.hash));
    window.addEventListener('hashchange', onChange);
    onChange();
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  return route;
}
